"use client";

import Link from "next/link";
import { useTranslations } from "next-intl";
import { Check, Plus, BookOpen, Calculator, Activity } from "lucide-react";
import { cn } from "@/lib/utils";
import { defaultLocale, formatPrice, isValidLocale } from "@/config/locale";
import { Badge } from "@/ui/components/ui/badge";
import { parsePeptideMetadata } from "@/lib/peptide-metadata";
import { translatePeptideFamily } from "@/lib/peptide-translations";
import type { PeptideProfile } from "@/config/peptide-stacks";
import { getPeptideLinks } from "@/config/peptide-links";
import type { ResolvedMoneyPresentation } from "@/lib/pricing/resolve-display-money";
import { ReferencePrimaryPrice } from "@/ui/components/pricing/reference-primary-price";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface VariantChoice {
	variantId: string;
	quantity: number;
}

interface PeptideCardProps {
	product: unknown;
	profile: PeptideProfile;
	isSelected: boolean;
	isDisabled: boolean;
	variantChoice: VariantChoice | null;
	onToggle: (slug: string) => void;
	onVariantChange: (slug: string, variantId: string) => void;
	onQuantityChange: (slug: string, quantity: number) => void;
	onRemove: (slug: string) => void;
	currency: string;
	locale: string;
	channel: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type ProductRecord = Record<string, unknown>;
type VariantRecord = Record<string, unknown>;

function isProductRecord(p: unknown): p is ProductRecord {
	return typeof p === "object" && p !== null;
}

function getVariants(product: unknown): VariantRecord[] {
	if (!isProductRecord(product)) return [];
	const variants = (product as ProductRecord).variants;
	if (!Array.isArray(variants)) return [];
	return variants as VariantRecord[];
}

function isOutOfStock(product: unknown): boolean {
	if (!isProductRecord(product)) return true;
	// Check product-level isAvailable first (most reliable)
	const isAvailable = (product as ProductRecord).isAvailable;
	if (typeof isAvailable === "boolean") return !isAvailable;
	// If quantityAvailable exists on variants, check it
	const variants = getVariants(product);
	if (variants.length === 0) return true;
	const hasQtyField = variants.some((v) => v.quantityAvailable != null);
	if (!hasQtyField) return false; // No stock data = assume available
	return !variants.some((v) => {
		const qty = v.quantityAvailable as number | undefined;
		return qty != null && qty > 0;
	});
}

function getCheapestPrice(product: unknown): number | null {
	if (!isProductRecord(product)) return null;
	// Try product-level pricing first (from ProductListItem fragment)
	const pricing = (product as ProductRecord).pricing as Record<string, unknown> | undefined;
	if (pricing) {
		const priceRange = pricing.priceRange as Record<string, unknown> | undefined;
		if (priceRange) {
			const start = priceRange.start as Record<string, unknown> | undefined;
			if (start) {
				const gross = start.gross as Record<string, unknown> | undefined;
				if (gross) {
					const amount = gross.amount as number | undefined;
					if (amount != null) return amount;
				}
			}
		}
	}
	// Fallback: check variant-level pricing
	const variants = getVariants(product);
	let min: number | null = null;
	for (const v of variants) {
		const vPricing = v.pricing as Record<string, unknown> | undefined;
		if (!vPricing) continue;
		const price = vPricing.price as Record<string, unknown> | undefined;
		if (!price) continue;
		const gross = price.gross as Record<string, unknown> | undefined;
		if (!gross) continue;
		const amount = gross.amount as number | undefined;
		if (amount == null) continue;
		if (min === null || amount < min) min = amount;
	}
	return min;
}

function getMetadata(product: unknown) {
	if (!isProductRecord(product)) return null;
	const metadata = (product as ProductRecord).metadata;
	if (!Array.isArray(metadata)) return null;
	return parsePeptideMetadata(metadata as Array<{ key?: string | null; value?: string | null }>);
}

// ---------------------------------------------------------------------------
// Research Score Bar
// ---------------------------------------------------------------------------

interface ResearchScoreBarProps {
	score: number; // 0–10 scale
}

function ResearchScoreBar({ score }: ResearchScoreBarProps) {
	const clamped = Math.max(0, Math.min(10, score));
	const pct = (clamped / 10) * 100;

	return (
		<div className="flex items-center gap-2">
			<div className="relative h-1.5 flex-1 overflow-hidden rounded-full bg-muted" role="presentation">
				<div
					className="absolute inset-y-0 left-0 rounded-full bg-accent transition-[width] duration-500"
					style={{ width: `${pct}%` }}
				/>
			</div>
			<span className="min-w-[2ch] text-right text-[10px] font-medium tabular-nums text-muted-foreground">
				{clamped}
			</span>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Category label mapping
// ---------------------------------------------------------------------------

const CATEGORY_LABEL_KEYS: Record<string, string> = {
	recovery: "goals.recovery",
	"anti-aging": "goals.antiAging",
	"weight-management": "goals.weightManagement",
	skin: "goals.skin",
	tanning: "goals.tanning",
	cognitive: "goals.cognitive",
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PeptideCard({
	product,
	profile,
	isSelected,
	isDisabled,
	onToggle,
	currency,
	locale,
	channel,
}: PeptideCardProps) {
	const t = useTranslations("stackBuilder");

	const outOfStock = isOutOfStock(product);
	const cheapest = getCheapestPrice(product);
	const cheapestPresentation =
		isProductRecord(product) && "stackBuilderCheapestPresentation" in product
			? ((product as { stackBuilderCheapestPresentation?: ResolvedMoneyPresentation | null })
					.stackBuilderCheapestPresentation ?? null)
			: null;
	const peptideMeta = getMetadata(product);
	const overallScore = peptideMeta?.scores.overall ?? null;
	const links = getPeptideLinks(profile.slug);

	const effectivelyDisabled = isDisabled || outOfStock;

	function handleClick() {
		if (effectivelyDisabled && !isSelected) return;
		onToggle(profile.slug);
	}

	const buttonLabel = isSelected
		? t("removeFromStackAria", { name: profile.name })
		: outOfStock
			? t("unavailableAria", { name: profile.name })
			: t("addToStackAria", { name: profile.name });

	return (
		<div
			className={cn(
				"group relative flex flex-col gap-3 rounded-lg border bg-card p-4 text-sm",
				"motion-safe:transition-transform motion-safe:active:scale-[0.98]",
				isSelected
					? "border-accent shadow-sm"
					: effectivelyDisabled
						? "border-border opacity-60"
						: "hover:border-border/80 border-border hover:shadow-sm",
			)}
		>
			{/* Header: name + family */}
			<div className="flex items-start justify-between gap-2">
				<div className="min-w-0">
					<p className="truncate text-base font-bold leading-tight text-foreground">{profile.name}</p>
					<p className="mt-0.5 truncate text-xs text-muted-foreground">
						{translatePeptideFamily(profile.family, locale)}
					</p>
				</div>

				{/* Out-of-stock badge */}
				{outOfStock && (
					<Badge variant="secondary" className="shrink-0 text-[10px]">
						{t("outOfStock")}
					</Badge>
				)}
			</div>

			{/* Category tags */}
			{profile.categories.length > 0 && (
				<div className="flex flex-wrap gap-1">
					{profile.categories.map((cat) => {
						const labelKey = CATEGORY_LABEL_KEYS[cat];
						return (
							<span
								key={cat}
								className="inline-flex items-center rounded-full border border-border bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground"
							>
								{labelKey ? t(labelKey as Parameters<typeof t>[0]) : cat}
							</span>
						);
					})}
				</div>
			)}

			{/* Research score bar */}
			{overallScore !== null && (
				<div className="space-y-0.5">
					<p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
						{t("researchScore")}
					</p>
					<ResearchScoreBar score={overallScore} />
				</div>
			)}

			{/* Guide + Calculator + Half-life deep-links */}
			{links && (links.guideSlug || links.calculatorSlug || links.halfLifeSlug) && (
				<div className="flex flex-wrap gap-3 text-[11px]">
					{links.guideSlug && (
						<Link
							href={`/${locale}/${channel}/guides/${links.guideSlug}`}
							onClick={(e) => e.stopPropagation()}
							className="inline-flex items-center gap-1 text-muted-foreground transition-colors hover:text-accent"
						>
							<BookOpen className="h-3 w-3" aria-hidden="true" />
							{t("links.guide")}
						</Link>
					)}
					{links.calculatorSlug && (
						<Link
							href={`/${locale}/${channel}/peptide-calculator/${links.calculatorSlug}`}
							onClick={(e) => e.stopPropagation()}
							className="inline-flex items-center gap-1 text-muted-foreground transition-colors hover:text-accent"
						>
							<Calculator className="h-3 w-3" aria-hidden="true" />
							{t("links.calculator")}
						</Link>
					)}
					{links.halfLifeSlug && (
						<Link
							href={`/${locale}/${channel}/half-life-calculator/${links.halfLifeSlug}`}
							onClick={(e) => e.stopPropagation()}
							className="inline-flex items-center gap-1 text-muted-foreground transition-colors hover:text-accent"
						>
							<Activity className="h-3 w-3" aria-hidden="true" />
							{t("links.halfLife")}
						</Link>
					)}
				</div>
			)}

			{/* Footer: price + toggle button */}
			<div className="mt-auto flex items-center justify-between gap-2">
				{/* Price */}
				<span className="text-xs text-muted-foreground">
					{cheapest !== null ? (
						<>
							<span className="mr-0.5 text-[10px]">{t("priceFromPrefix")}</span>
							<span className="font-semibold text-foreground">
								{cheapestPresentation ? (
									<ReferencePrimaryPrice
										presentation={cheapestPresentation}
										locale={locale}
										channel={channel}
										size="sm"
										className="font-semibold text-foreground"
									/>
								) : (
									formatPrice(cheapest, currency, isValidLocale(locale) ? locale : defaultLocale)
								)}
							</span>
						</>
					) : (
						<span className="text-[10px]">—</span>
					)}
				</span>

				{/* Toggle button */}
				<button
					type="button"
					onClick={handleClick}
					disabled={effectivelyDisabled && !isSelected}
					aria-label={buttonLabel}
					className={cn(
						"inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-semibold",
						"transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
						"disabled:cursor-not-allowed disabled:opacity-50",
						isSelected
							? "hover:bg-accent/90 border-accent bg-accent text-accent-foreground"
							: effectivelyDisabled
								? "cursor-not-allowed border-border bg-muted text-muted-foreground"
								: "hover:border-accent/60 hover:bg-accent/5 border-border bg-background text-foreground",
					)}
				>
					{isSelected ? (
						<>
							<Check className="h-3 w-3" aria-hidden="true" />
							<span>{t("inStack")}</span>
						</>
					) : (
						<>
							<Plus className="h-3 w-3" aria-hidden="true" />
							<span>{t("addToStack")}</span>
						</>
					)}
				</button>
			</div>
		</div>
	);
}
